import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { PhoneData } from '@/types'
import { MessagingMethodCode } from '@/domain/messagingMethods/messagingMethodsTypes'

interface SignUpForm {
  userId?: string
  verifyToken?: string
}

interface SignInForm {
  userId?: string
}

interface RecoverPasswordForm {
  userId?: string
  email?: string
  phone?: string
  recoveryMethod?: 'email' | 'phone'
  messagingMethod?: {
    code?: string
    imgUrl?: string
  }
  verifyToken?: string
}

interface AuthScopedStore {
  email?: string
  phone?: PhoneData | null
  identificationMethod?: 'email' | 'phone' | 'google' | 'apple'
  messagingMethod?: MessagingMethodCode
  signUpForm: SignUpForm
  signInForm: SignInForm
  recoverPasswordForm: RecoverPasswordForm

  setEmail: (email?: string) => void
  setPhone: (phone?: PhoneData | null) => void
  setIdentificationMethod: (
    method?: 'email' | 'phone' | 'google' | 'apple',
  ) => void
  setMessagingMethod: (messagingMethod: MessagingMethodCode) => void
  setSignUpForm: (form: SignUpForm) => void
  setSignInForm: (form: SignInForm) => void
  setRecoverPasswordForm: (form: Partial<RecoverPasswordForm>) => void
  resetRecoverPasswordForm: () => void
  reset: () => void
}

const initialState = {
  email: undefined,
  phone: undefined,
  identificationMethod: undefined,
  signUpForm: {},
  signInForm: {},
  recoverPasswordForm: {},
  messagingMethod: undefined,
}

export const useAuthScopedStore = create<AuthScopedStore>()(
  persist(
    (set) => ({
      ...initialState,

      setEmail: (email?: string) => set((state) => ({ ...state, email })),

      setPhone: (phone?: PhoneData | null) =>
        set((state) => ({ ...state, phone })),

      setIdentificationMethod: (
        identificationMethod?: 'email' | 'phone' | 'google' | 'apple',
      ) => set((state) => ({ ...state, identificationMethod })),

      setSignUpForm: (signUpForm: SignUpForm) => {
        set((state) => ({
          ...state,
          signUpForm: { ...state.signUpForm, ...signUpForm },
        }))
      },

      setMessagingMethod: (messagingMethod: MessagingMethodCode) =>
        set((state) => ({ ...state, messagingMethod })),

      setSignInForm: (signInForm: SignInForm) =>
        set((state) => ({ ...state, signInForm })),

      setRecoverPasswordForm: (
        recoverPasswordForm: Partial<RecoverPasswordForm>,
      ) =>
        set((state) => ({
          ...state,
          recoverPasswordForm: {
            ...state.recoverPasswordForm,
            ...recoverPasswordForm,
          },
        })),

      resetRecoverPasswordForm: () =>
        set((state) => ({ ...state, recoverPasswordForm: {} })),

      reset: () => set(initialState),
    }),
    {
      name: 'auth-scoped',
      storage: {
        getItem: (name) => {
          const str = sessionStorage.getItem(name) // Use sessionStorage for temporary data
          return str ? JSON.parse(str) : null
        },
        setItem: (name, value) => {
          sessionStorage.setItem(name, JSON.stringify(value))
        },
        removeItem: (name) => sessionStorage.removeItem(name),
      },
    },
  ),
)
